import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Row as BootstrapRow, Col as BootstrapCol, Modal, Alert } from 'react-bootstrap';
import { Table, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import PageTitle from '@/common/PageTitle/PageTitle';
import FormItem from '@/common/FormItem/FormItem';
import styles from '@/styles/inventory-products.module.scss';
import pageStyles from './inventory-products.module.scss';

const products = [
  {
    key: '1',
    image: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=facearea&w=64&h=64',
    name: 'KrossKast Player 8',
    status: 'Active',
    stock: '12 in stock',
    category: 'KK Players',
    location: '3 stores',
    faded: false,
  },
  {
    key: '2',
    image: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=facearea&w=64&h=64',
    name: 'KrossKast Player 11',
    status: 'Active',
    stock: '10 in stock',
    category: 'KK Players',
    location: '4 stores',
    faded: false,
  },
];

const columns: ColumnsType<any> = [
  {
    title: '',
    dataIndex: 'checkbox',
    width: 20,
    align: 'center',
    className: 'checkbox-col',
    render: (_: any, record: any) => (
      <input
        type="checkbox"
        className={styles.checkbox}
        style={{ margin: 0 }}
      />
    ),
  },
  {
    title: 'Name of product',
    dataIndex: 'name',
    render: (_: any, record: any) => (
      <span className={styles.productName}>
        <img src={record.image} alt={record.name} className={styles.productImage} />
        <span className={styles.productNameText}>{record.name}</span>
      </span>
    ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (status: string) => {
      const statusClass = status === 'Active' ? styles.statusActive : 
                         status === 'Sold out' ? styles.statusSoldOut : 
                         styles.statusLowStock;
      return (
        <span className={`${styles.statusBadge} ${statusClass}`}>
          {status}
        </span>
      );
    },
  },
  {
    title: 'Stock info',
    dataIndex: 'stock',
    render: (stock: string) => <span className={styles.stockInfo}>{stock}</span>,
  },
  {
    title: 'Category',
    dataIndex: 'category',
    render: (category: string) => <span className={styles.category}>{category}</span>,
  },
  {
    title: 'Location',
    dataIndex: 'location',
    render: (location: string) => <span className={styles.location}>{location}</span>,
  },
];

const InventoryProducts: React.FC = () => {
  const navigate = useNavigate();
  
  // Filter state
  const [filters, setFilters] = useState({
    productName: undefined as string | undefined,
    category: undefined as string | undefined,
    stockInfo: undefined as string | undefined,
  });
  const [resetCounter, setResetCounter] = useState(0);

  // Add Product Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    itemCode: '',
    stockSize: '',
    warehouseAvailability: '',
    image: null as File | null
  });

  // Filter options
  const filterOptions = {
    productNames: products.map(product => product.name),
    categories: [...new Set(products.map(product => product.category))],
    stockInfos: [...new Set(products.map(product => product.stock))],
  };

  // Filtering logic
  const filteredProducts = products.filter(product => {
    return (
      (!filters.productName || product.name === filters.productName) &&
      (!filters.category || product.category === filters.category) &&
      (!filters.stockInfo || product.stock === filters.stockInfo)
    );
  });

  // Filter change handlers
  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({ productName: undefined, category: undefined, stockInfo: undefined });
    setResetCounter(prev => prev + 1);
  };

  // Add Product Modal handlers
  const handleInputChange = (field: string, value: string) => {
    setProductForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setProductForm(prev => ({ ...prev, image: file }));
  };

  const handleAddProduct = () => {
    // Validation
    if (!productForm.name.trim()) {
      setAlertMessage('Product name is required');
      setShowErrorAlert(true);
      return;
    }
    
    if (!productForm.category.trim()) {
      setAlertMessage('Category is required');
      setShowErrorAlert(true);
      return;
    }
    
    if (!productForm.price.trim()) {
      setAlertMessage('Price is required');
      setShowErrorAlert(true);
      return;
    }
    
    if (!productForm.itemCode.trim()) {
      setAlertMessage('Item code is required');
      setShowErrorAlert(true);
      return;
    }
    
    if (!productForm.stockSize.trim()) {
      setAlertMessage('Stock size is required');
      setShowErrorAlert(true);
      return;
    }
    
    if (!productForm.warehouseAvailability) {
      setAlertMessage('Warehouse availability is required');
      setShowErrorAlert(true);
      return;
    }

    // Simulate product creation
    console.log('Creating product:', productForm);
    
    // Reset form
    setProductForm({
      name: '',
      description: '',
      category: '',
      price: '',
      itemCode: '',
      stockSize: '',
      warehouseAvailability: '',
      image: null
    });
    
    setShowAddModal(false);
    setAlertMessage('Product added successfully!');
    setShowSuccessAlert(true);
  };

  return (
    <Container fluid>
      <Card className={pageStyles.card}>
        <Card.Body>
          <Card.Title className={pageStyles.cardTitle}>
            <PageTitle title="Products" />
            <Button 
              onClick={handleReset}
              className={pageStyles.resetButton}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          
          <div className={pageStyles.form}>
            <BootstrapRow>
              <BootstrapCol>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Name of Product"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`productName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${pageStyles.selectField}`}
                            placeholder="Any product name"
                            value={filters.productName}
                            onChange={value => handleFilterChange('productName', value)}
                            options={filterOptions.productNames.map(name => ({ label: name, value: name }))}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Category"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`category-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${pageStyles.selectField}`}
                            placeholder="Any category"
                            value={filters.category}
                            onChange={value => handleFilterChange('category', value)}
                            options={filterOptions.categories.map(category => ({ label: category, value: category }))}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Stock Info"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`stockInfo-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${pageStyles.selectField}`}
                            placeholder="Any stock info"
                            value={filters.stockInfo}
                            onChange={value => handleFilterChange('stockInfo', value)}
                            options={filterOptions.stockInfos.map(stock => ({ label: stock, value: stock }))}
                          />
                        </FormItem>
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol 
                        md={12}
                        className="d-flex justify-content-end"
                      >
                        <Button
                          style={{ 
                            background: 'white', 
                            color: '#e21e26', 
                            border: '1px solid #e21e26', 
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            justifyContent: 'center',
                            height: '50px',
                            marginTop: '8px'
                          }}
                          onClick={() => setShowAddModal(true)}
                        >
                          <PlusOutlined style={{ fontSize: 16 }} />
                          Add New Product
                        </Button>
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
              </BootstrapCol>
            </BootstrapRow>
          </div>
          
          {/* Products Table */}
          <Table
            className={`${styles.table} ${styles.legacyRowHeightTable}`}
            columns={columns}
            dataSource={filteredProducts}
            pagination={false}
            rowClassName={record => record.faded ? 'faded-row' : ''}
            scroll={{ x: true }}
            rowKey="key"
            bordered
            onRow={(record) => ({
              onClick: () => navigate(`/inventory-products/${record.key}`),
              className: styles.clickableRow,
            })}
            components={{
              header: {
                cell: (props: any) => (
                  <th
                    {...props}
                    className={pageStyles.tableHeader}
                  />
                )
              }
            }}
          />
        </Card.Body>
      </Card>

      {/* Success Alert */}
      {showSuccessAlert && (
        <Alert 
          variant="success" 
          onClose={() => setShowSuccessAlert(false)} 
          dismissible
          className="mt-3"
        >
          {alertMessage}
        </Alert>
      )}

      {/* Error Alert */}
      {showErrorAlert && (
        <Alert 
          variant="danger" 
          onClose={() => setShowErrorAlert(false)} 
          dismissible
          className="mt-3"
        >
          {alertMessage}
        </Alert>
      )}

      {/* Add Product Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter product name"
                    value={productForm.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Enter product description"
                    value={productForm.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Category *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter category"
                    value={productForm.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Price *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter price"
                    value={productForm.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Item code *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter item code"
                    value={productForm.itemCode}
                    onChange={(e) => handleInputChange('itemCode', e.target.value)}
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Stock size *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter stock size"
                    value={productForm.stockSize}
                    onChange={(e) => handleInputChange('stockSize', e.target.value)}
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Warehouse availability *</Form.Label>
                  <Form.Select
                    value={productForm.warehouseAvailability}
                    onChange={(e) => handleInputChange('warehouseAvailability', e.target.value)}
                    required
                  >
                    <option value="">Select warehouse...</option>
                    <option value="georgia-us">Georgia, US</option>
                    <option value="florida-us">Florida, US</option>
                  </Form.Select>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Product photos</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <Form.Text className="text-muted">
                    Upload product images (optional)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddProduct}>
            Add Product
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default InventoryProducts; 